import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Lock,
  LogOut,
  Loader2,
  Check,
  X,
  Landmark,
  Trash2,
  CalendarDays,
  Clock,
  Users,
  MapPin,
  Phone,
  Mail,
  BellRing,
  CalendarOff,
  Plus,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import AdminEvents from "@/components/AdminEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lenslopers_admin_token";

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Er ging iets mis. Probeer het opnieuw.";
};

const STATUS_LABEL = {
  pending: "Nieuw",
  approved: "Goedgekeurd",
  declined: "Geweigerd",
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-screen bg-ink flex items-center justify-center px-6 py-32">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm relative bg-ink border border-white/10 p-9"
      >
        <span className="absolute top-0 left-0 w-10 h-px bg-gold" />
        <span className="absolute top-0 left-0 h-10 w-px bg-gold" />
        <span className="absolute bottom-0 right-0 w-10 h-px bg-gold" />
        <span className="absolute bottom-0 right-0 h-10 w-px bg-gold" />

        <Lock size={20} className="text-gold" strokeWidth={1.4} />
        <p className="eyebrow text-gold mt-6">Beheer</p>
        <h1 className="font-serif text-3xl text-white mt-2 leading-tight">Meld je aan</h1>

        <label className="block mt-9">
          <span className="eyebrow block mb-2 text-white/40">E-mail</span>
          <input
            data-testid="admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-b border-white/25 focus:border-gold outline-none py-2 text-white"
          />
        </label>
        <label className="block mt-6">
          <span className="eyebrow block mb-2 text-white/40">Wachtwoord</span>
          <input
            data-testid="admin-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-white/25 focus:border-gold outline-none py-2 text-white"
          />
        </label>

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="btn-gold w-full mt-10 disabled:opacity-60"
        >
          {busy ? "Bezig..." : "Aanmelden"}
        </button>
      </motion.form>
    </section>
  );
};

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const parseKey = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const BlockedDates = () => {
  const [dates, setDates] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [booked, setBooked] = useState([]);
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, av] = await Promise.all([
        axios.get(`${API}/admin/blocked-dates`, { headers: authHeaders() }),
        axios.get(`${API}/magic-mirror/availability`),
      ]);
      setDates(b.data);
      setBooked(av.data.booked || []);
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const block = async () => {
    if (!selected) {
      toast.error("Kies eerst een dag in de kalender.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/blocked-dates`,
        { date: toKey(selected), reason: reason || null },
        { headers: authHeaders() }
      );
      toast.success("Dag geblokkeerd — bezoekers kunnen deze datum niet meer kiezen.");
      setSelected(null);
      setReason("");
      await load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (date) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/blocked-dates/${date}`, { headers: authHeaders() });
      toast.success("Dag weer vrijgegeven.");
      await load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white py-16 md:py-20 border-t border-neutral-200">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="eyebrow mb-3">Eigen agenda</p>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight">
            Blokkeer dagen die <span className="italic text-gold">niet kunnen.</span>
          </h2>
          <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
            Vakantie, een andere opdracht of gewoon een vrije dag? Klik een dag aan en blokkeer
            hem — bezoekers zien die dag dan meteen als niet beschikbaar in de kalender.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
          <div className="lg:col-span-6">
            <div className="bg-beige p-2 sm:p-4 inline-block" data-testid="admin-block-calendar">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={setSelected}
                month={month}
                onMonthChange={setMonth}
                fromDate={new Date()}
                disabled={[
                  { before: new Date() },
                  ...booked.map(parseKey),
                  ...dates.map((d) => parseKey(d.date)),
                ]}
                classNames={{
                  day_selected:
                    "bg-gold text-white hover:bg-gold hover:text-white focus:bg-gold focus:text-white",
                  day_disabled:
                    "text-neutral-400 line-through opacity-100 hover:bg-transparent cursor-not-allowed",
                }}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-6 max-w-md">
              <input
                data-testid="admin-block-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={120}
                placeholder="Reden (optioneel) — bv. vakantie"
                className="flex-1 border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent text-sm"
              />
              <button
                data-testid="admin-block-submit"
                onClick={block}
                disabled={busy || !selected}
                className="inline-flex items-center justify-center gap-2 bg-ink text-gold px-5 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-black transition-colors disabled:opacity-50"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Blokkeer dag
              </button>
            </div>
          </div>

          <div className="lg:col-span-6">
            <p className="eyebrow mb-5 text-neutral-500">Geblokkeerde dagen</p>
            {dates.length === 0 ? (
              <div className="border border-dashed border-neutral-300 p-8 text-center">
                <CalendarOff size={22} className="text-gold mx-auto" strokeWidth={1.3} />
                <p className="text-sm text-neutral-500 mt-4">
                  Nog geen dagen geblokkeerd — je agenda staat volledig open.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-neutral-200" data-testid="admin-blocked-list">
                {dates.map((d) => (
                  <li key={d.date} className="flex items-center justify-between gap-4 py-4">
                    <div>
                      <p className="font-serif text-lg text-ink">{d.date}</p>
                      {d.reason && <p className="text-xs text-neutral-500 mt-1">{d.reason}</p>}
                    </div>
                    <button
                      data-testid={`admin-unblock-${d.date}`}
                      onClick={() => unblock(d.date)}
                      disabled={busy}
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500 hover:text-gold transition-colors disabled:opacity-50"
                    >
                      <X size={13} />
                      Vrijgeven
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ icon: Icon, children }) => (
  <p className="flex items-start gap-2.5 text-sm text-ink/70">
    <Icon size={14} className="text-gold mt-0.5 shrink-0" />
    <span>{children}</span>
  </p>
);

const ReservationCard = ({ r, onAction, busyId }) => {
  const busy = busyId === r.id;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`admin-reservation-${r.id}`}
      className="bg-white border border-neutral-200 p-6 md:p-7"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-serif text-2xl text-ink leading-tight">{r.name}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-2">
            {r.event_type} — {r.hours} uur vanaf {r.start_time}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span
            className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 ${
              r.status === "approved"
                ? "bg-gold text-white"
                : r.status === "declined"
                ? "bg-neutral-200 text-neutral-600"
                : "bg-ink text-gold"
            }`}
          >
            {STATUS_LABEL[r.status] || r.status}
          </span>
          {r.deposit_paid && (
            <span className="text-[10px] uppercase tracking-[0.2em] text-gold">
              Voorschot ontvangen
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-6">
        <Row icon={CalendarDays}>{r.date}</Row>
        <Row icon={Clock}>
          {r.start_time} — {r.hours} uur
        </Row>
        <Row icon={MapPin}>{r.location}</Row>
        <Row icon={Users}>{r.guests ? `${r.guests} gasten` : "aantal gasten onbekend"}</Row>
        <Row icon={Mail}>
          <a href={`mailto:${r.email}`} className="hover:text-gold">
            {r.email}
          </a>
        </Row>
        <Row icon={Phone}>
          <a href={`tel:${r.phone}`} className="hover:text-gold">
            {r.phone}
          </a>
        </Row>
        {r.payment_reference && <Row icon={Landmark}>Mededeling: {r.payment_reference}</Row>}
      </div>

      {r.notes && (
        <p className="mt-5 bg-beige p-4 text-sm text-ink/70 leading-relaxed border-l-2 border-gold">
          {r.notes}
        </p>
      )}

      <div className="flex flex-wrap gap-3 mt-7 pt-5 border-t border-neutral-200">
        {r.status === "pending" && (
          <>
            <button
              data-testid={`admin-approve-${r.id}`}
              disabled={busy}
              onClick={() => onAction(r.id, "decision", { action: "approve" })}
              className="inline-flex items-center gap-2 bg-gold text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Goedkeuren
            </button>
            <button
              data-testid={`admin-decline-${r.id}`}
              disabled={busy}
              onClick={() => onAction(r.id, "decision", { action: "decline" })}
              className="inline-flex items-center gap-2 border border-ink px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
            >
              <X size={13} />
              Weigeren
            </button>
          </>
        )}
        {r.status === "approved" && !r.deposit_paid && (
          <>
            <button
              data-testid={`admin-deposit-${r.id}`}
              disabled={busy}
              onClick={() => onAction(r.id, "deposit-paid")}
              className="inline-flex items-center gap-2 bg-ink text-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-black transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Landmark size={13} />}
              Voorschot ontvangen
            </button>
            <button
              data-testid={`admin-reminder-${r.id}`}
              disabled={busy}
              onClick={() => onAction(r.id, "reminder")}
              className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
            >
              <BellRing size={13} />
              Herinnering sturen
            </button>
          </>
        )}
        <button
          data-testid={`admin-delete-${r.id}`}
          disabled={busy}
          onClick={() => onAction(r.id, "delete")}
          className="inline-flex items-center gap-2 text-neutral-400 px-2 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:text-red-700 transition-colors disabled:opacity-50 ml-auto"
        >
          <Trash2 size={13} />
          Verwijderen
        </button>
      </div>
    </motion.div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [reservations, setReservations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, sum] = await Promise.all([
        axios.get(`${API}/admin/reservations`, { headers: authHeaders() }),
        axios.get(`${API}/admin/summary`, { headers: authHeaders() }),
      ]);
      setReservations(res.data);
      setSummary(sum.data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const onAction = async (id, kind, body) => {
    setBusyId(id);
    try {
      if (kind === "delete") {
        await axios.delete(`${API}/admin/reservations/${id}`, { headers: authHeaders() });
        toast.success("Reservatie verwijderd.");
      } else if (kind === "reminder") {
        await axios.post(`${API}/admin/reservations/${id}/reminder`, null, {
          headers: authHeaders(),
        });
        toast.success("Herinnering verstuurd naar de klant.");
      } else if (kind === "deposit-paid") {
        await axios.post(`${API}/admin/reservations/${id}/deposit-paid`, null, {
          headers: authHeaders(),
        });
        toast.success("Voorschot geregistreerd — klant kreeg de bevestiging.");
      } else {
        await axios.post(`${API}/admin/reservations/${id}/decision`, body, {
          headers: authHeaders(),
        });
        toast.success(
          body.action === "approve"
            ? "Goedgekeurd — klant kreeg de betaalgegevens."
            : "Geweigerd — klant is verwittigd."
        );
      }
      await load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusyId(null);
    }
  };

  const filtered =
    filter === "all" ? reservations : reservations.filter((r) => r.status === filter);

  const stats = summary
    ? [
        { label: "Nieuwe aanvragen", value: summary.counts.pending },
        { label: "Wacht op voorschot", value: summary.counts.awaiting_deposit },
        { label: "Voorschot ontvangen", value: summary.counts.deposit_paid },
        { label: "Goedgekeurd", value: summary.counts.approved },
      ]
    : [];

  return (
    <>
      <section className="bg-ink text-white pt-40 md:pt-44 pb-16">
        <div className="container-x">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <p className="eyebrow text-gold mb-3">Beheer</p>
              <h1 className="font-serif text-4xl md:text-5xl leading-tight">
                Magic Mirror <span className="italic text-gold">reservaties</span>
              </h1>
              <p className="mt-4 text-white/50 text-sm">Aangemeld als {user.email}</p>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-white/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors"
            >
              <LogOut size={13} />
              Afmelden
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-14" data-testid="admin-stats">
            {stats.map((s) => (
              <div key={s.label} className="border border-white/10 p-5">
                <p className="font-serif text-4xl text-gold">{s.value}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-3">
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          {summary && (
            <p className="mt-8 text-xs text-white/40 leading-relaxed">
              Voorschot €{summary.deposit_amount.toFixed(0)} via overschrijving op{" "}
              {summary.iban || "—"} ({summary.holder || "—"}
              {summary.bic ? `, BIC ${summary.bic}` : ""}) &middot; waarborg €
              {summary.security_deposit.toFixed(0)} cash of overschrijving op de dag van het feest
            </p>
          )}
        </div>
      </section>

      <section className="bg-beige py-16 md:py-20 min-h-[50vh]">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-10">
            {[
              ["pending", "Nieuw"],
              ["approved", "Goedgekeurd"],
              ["declined", "Geweigerd"],
              ["all", "Alles"],
            ].map(([id, label]) => (
              <button
                key={id}
                data-testid={`admin-filter-${id}`}
                onClick={() => setFilter(id)}
                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  filter === id
                    ? "bg-ink text-gold"
                    : "bg-white text-ink hover:text-gold border border-neutral-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="inline-flex items-center gap-2 text-neutral-500 text-sm">
              <Loader2 size={15} className="animate-spin" />
              Laden...
            </p>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-neutral-300 p-14 text-center">
              <CalendarDays size={26} className="text-gold mx-auto" strokeWidth={1.3} />
              <p className="font-serif text-2xl text-ink mt-6">Nog niets hier</p>
              <p className="text-sm text-neutral-500 mt-2">
                Zodra iemand het reservatieformulier invult, verschijnt de aanvraag hier.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" data-testid="admin-reservations">
              {filtered.map((r) => (
                <ReservationCard key={r.id} r={r} onAction={onAction} busyId={busyId} />
              ))}
            </div>
          )}
        </div>
      </section>

      <BlockedDates />
      <AdminEvents />
    </>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <section className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 size={22} className="text-gold animate-spin" />
      </section>
    );
  }

  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
